import React, { useState, useEffect, useCallback } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Progress } from '../../components/ui/progress';
import { Badge } from '../../components/ui/badge';
import { 
  TrendingUp, 
  TrendingDown, 
  DollarSign, 
  Clock, 
  AlertTriangle,
  CheckCircle2,
  ArrowRight,
  Building2,
  BarChart3,
  RefreshCw,
  Shield
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function FundabilityDashboard() {
  const [loading, setLoading] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [fundabilityData, setFundabilityData] = useState(null);
  const [simulation, setSimulation] = useState(null);
  const [ipMode, setIpMode] = useState(false);

  // Sample input data (in production, this would come from user's actual data)
  const sampleInputData = {
    structure_score: 75,
    dnb_data: {
      paydex: 70,
      delinquency: 2
    },
    nav_data: {
      score: 65,
      utilization: 60
    },
    industry: "professional_services"
  };

  const calculateFundability = useCallback(async () => {
    setCalculating(true);
    try {
      const token = localStorage.getItem('corpcraft_token');
      
      const response = await axios.post(
        `${API_URL}/api/developer/calculate-fundability`,
        {
          ...sampleInputData,
          ip_mode: ipMode
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      setFundabilityData(response.data);
      toast.success('Fundability score calculated successfully');

      // Also get optimization recommendations
      if (response.data.fundability_score) {
        const optimizeResponse = await axios.post(
          `${API_URL}/api/developer/optimize-fundability`,
          {
            current_score: response.data.fundability_score,
            structure_score: sampleInputData.structure_score,
            dnb_data: sampleInputData.dnb_data,
            nav_data: sampleInputData.nav_data,
            ip_mode: ipMode
          },
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );

        setSimulation(optimizeResponse.data.simulation);
      }
    } catch (error) {
      console.error('Fundability calculation error:', error);
      toast.error(error.response?.data?.detail || 'Failed to calculate fundability');
    } finally {
      setCalculating(false);
    }
  }, [ipMode]);

  useEffect(() => {
    calculateFundability();
  }, []);

  const getGradeColor = (grade) => {
    if (grade.startsWith('A')) return 'text-green-400';
    if (grade.startsWith('B')) return 'text-yellow-400';
    return 'text-red-400';
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'critical': return 'destructive';
      case 'high': return 'default';
      case 'medium': return 'secondary';
      default: return 'outline';
    }
  };

  if (loading || !fundabilityData) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <div className="flex flex-col items-center gap-4">
            <RefreshCw className="w-8 h-8 text-blue-500 animate-spin" />
            <p className="text-slate-400">Calculating fundability score...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Credit Intelligence</h1>
            <p className="text-slate-400 mt-1">
              Fundability score and optimization insights
            </p>
          </div>
          <div className="flex items-center gap-4">
            {/* IP Mode Toggle */}
            <div className="flex items-center gap-3 px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg">
              <Shield className="w-5 h-5 text-blue-400" />
              <span className="text-sm text-slate-300">IP Protection Mode</span>
              <button
                onClick={() => {
                  setIpMode(!ipMode);
                  toast.info(ipMode ? 'IP Mode disabled' : 'IP Mode enabled');
                }}
                className={`relative w-11 h-6 rounded-full transition-colors ${
                  ipMode ? 'bg-blue-500' : 'bg-slate-600'
                }`}
              >
                <div 
                  className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${
                    ipMode ? 'transform translate-x-5' : ''
                  }`}
                />
              </button>
            </div>
            
            <Button onClick={calculateFundability} disabled={calculating}>
              <RefreshCw className={`w-4 h-4 mr-2 ${calculating ? 'animate-spin' : ''}`} />
              Recalculate
            </Button>
          </div>
        </div>

        {/* Main Score Card */}
        <Card className="bg-gradient-to-br from-slate-900 to-slate-800 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white">Fundability Score</CardTitle>
            <CardDescription>Your overall corporate fundability rating</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-baseline gap-2">
                  <span className="text-6xl font-bold text-white">
                    {fundabilityData.fundability_score}
                  </span>
                  <span className="text-2xl text-slate-400">/100</span>
                </div>
                <div className={`text-2xl font-semibold mt-2 ${getGradeColor(fundabilityData.grade)}`}>
                  Grade: {fundabilityData.grade}
                </div>
              </div>
              
              {simulation && (
                <div className="text-right">
                  <div className="text-sm text-slate-400 mb-1">Projected Score</div>
                  <div className="flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-green-400" />
                    <span className="text-3xl font-bold text-green-400">
                      {simulation.projected_score}
                    </span>
                  </div>
                  <div className="text-sm text-green-400 mt-1">
                    +{simulation.improvement} points
                  </div>
                  <div className="text-xs text-slate-500 mt-1">
                    {simulation.estimated_timeline}
                  </div>
                </div>
              )}
            </div>

            {/* Progress Bar */}
            <div className="mt-6">
              <Progress value={fundabilityData.fundability_score} className="h-2" />
            </div>
          </CardContent>
        </Card>

        {/* Score Breakdown */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="bg-slate-900 border-slate-700">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm text-slate-400">Structure Score</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-2xl font-bold text-white">
                    {fundabilityData.breakdown.structure.score}
                  </div>
                  <div className="text-xs text-slate-500 mt-1">
                    {fundabilityData.breakdown.structure.weight}% weight
                  </div>
                </div>
                <Building2 className="w-8 h-8 text-blue-400" />
              </div>
              <div className="text-xs text-slate-400 mt-2">
                Contribution: +{fundabilityData.breakdown.structure.contribution}
              </div>
            </CardContent>
          </Card>

          <Card className="bg-slate-900 border-slate-700">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm text-slate-400">D&B Paydex</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-2xl font-bold text-white">
                    {fundabilityData.breakdown.dnb_paydex.score}
                  </div>
                  <div className="text-xs text-slate-500 mt-1">
                    {fundabilityData.breakdown.dnb_paydex.weight}% weight
                  </div>
                </div>
                <BarChart3 className="w-8 h-8 text-purple-400" />
              </div>
              <div className="text-xs text-slate-400 mt-2">
                Contribution: +{fundabilityData.breakdown.dnb_paydex.contribution}
              </div>
            </CardContent>
          </Card>

          <Card className="bg-slate-900 border-slate-700">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm text-slate-400">Nav Score</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-2xl font-bold text-white">
                    {fundabilityData.breakdown.nav_score.score}
                  </div>
                  <div className="text-xs text-slate-500 mt-1">
                    {fundabilityData.breakdown.nav_score.weight}% weight
                  </div>
                </div>
                <TrendingUp className="w-8 h-8 text-green-400" />
              </div>
              <div className="text-xs text-slate-400 mt-2">
                Contribution: +{fundabilityData.breakdown.nav_score.contribution}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Matched Lenders */}
        <Card className="bg-slate-900 border-slate-700">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-green-400" />
              Matched Lenders
            </CardTitle>
            <CardDescription>
              Lenders you qualify for based on your fundability score
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {fundabilityData.matched_lenders?.map((lender, index) => (
                <div 
                  key={index}
                  className="flex items-center justify-between p-4 bg-slate-800/50 rounded-lg border border-slate-700 hover:border-slate-600 transition-colors"
                >
                  <div className="flex-1">
                    <div className="font-semibold text-white">{lender.name}</div>
                    <div className="text-sm text-slate-400 mt-1">
                      Amount: {lender.typical_amount} • Speed: {lender.speed}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <div className="text-sm text-slate-400">Match Strength</div>
                      <div className="text-lg font-bold text-green-400">
                        {lender.match_strength}%
                      </div>
                    </div>
                    <ArrowRight className="w-5 h-5 text-slate-500" />
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Recommendations */}
        <Card className="bg-slate-900 border-slate-700">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-blue-400" />
              Optimization Recommendations
            </CardTitle>
            <CardDescription>
              Actions to improve your fundability score
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {fundabilityData.recommendations?.map((rec, index) => (
                <div 
                  key={rec.id}
                  className="p-4 bg-slate-800/50 rounded-lg border border-slate-700"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <Badge variant={getPriorityColor(rec.priority)}>
                        {rec.priority}
                      </Badge>
                      <span className="text-xs text-slate-500">{rec.category}</span>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-semibold text-green-400">
                        {rec.impact_range}
                      </div>
                      <div className="text-xs text-slate-500">{rec.timeline}</div>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="w-4 h-4 text-yellow-400 mt-0.5 flex-shrink-0" />
                      <div>
                        <span className="text-sm text-slate-400">Issue: </span>
                        <span className="text-sm text-white">{rec.issue}</span>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
                      <div>
                        <span className="text-sm text-slate-400">Action: </span>
                        <span className="text-sm text-white">{rec.action}</span>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-4 mt-3 pt-3 border-t border-slate-700">
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-slate-500" />
                        <span className="text-xs text-slate-400">{rec.timeline}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <BarChart3 className="w-4 h-4 text-slate-500" />
                        <span className="text-xs text-slate-400">
                          Difficulty: {rec.difficulty}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
